import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Users, Banknote } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { Reveal, Overline } from "../Reveal";

export default function Vacancies() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [country, setCountry] = useState("all");

  useEffect(() => {
    api.get("/vacancies").then((r) => setVacancies(r.data)).catch(() => {});
  }, []);

  const countries = useMemo(() => ["all", ...new Set(vacancies.map((v) => v.country))], [vacancies]);
  const filtered = country === "all" ? vacancies : vacancies.filter((v) => v.country === country);

  return (
    <section className="relative py-28 px-6 sm:px-12" data-testid="vacancies-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Overline>{t("vacancies.overline")}</Overline>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("vacancies.title")}</h2>
              <p className="text-[#1B2A4E]/60 mt-3 max-w-xl">{t("vacancies.sub")}</p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="country-filters">
              {countries.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-country-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setCountry(c)}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                    country === c
                      ? "bg-[#1B2A4E] text-white"
                      : "border border-[#1B2A4E]/20 text-[#1B2A4E]/60 hover:text-[#1B2A4E] hover:border-[#E8842C]"
                  }`}
                >
                  {c === "all" ? t("vacancies.all") : c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        {filtered.length === 0 ? (
          <p className="text-[#1B2A4E]/40 text-center py-16" data-testid="vacancies-empty">{t("vacancies.empty")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.06}>
                <div data-testid={`vacancy-card-${i}`} className="card-light p-7 group relative">
                  <div className="absolute top-0 start-0 w-14 h-1 bg-gradient-to-r from-[#E8842C] to-[#F5A623] rounded-full" />
                  <h3 className="font-head font-bold text-lg text-[#1B2A4E] group-hover:text-[#E8842C] transition-colors">{v.title}</h3>
                  <p className="text-xs text-[#E8842C] font-bold mt-1 uppercase tracking-wider">{v.industry}</p>
                  <div className="mt-5 space-y-2.5 text-sm text-[#1B2A4E]/65">
                    <p className="flex items-center gap-2.5"><MapPin className="w-4 h-4 text-[#E8842C]" /> {v.country}</p>
                    <p className="flex items-center gap-2.5"><Banknote className="w-4 h-4 text-[#E8842C]" /> {v.salary} {v.currency} <span className="text-[10px] uppercase tracking-wider text-[#1B2A4E]/40">/ {t("vacancies.salary")}</span></p>
                    <p className="flex items-center gap-2.5"><Users className="w-4 h-4 text-[#E8842C]" /> {v.openings} {t("vacancies.openings")}</p>
                  </div>
                  <button
                    data-testid={`vacancy-apply-${i}`}
                    onClick={() => navigate("/employee")}
                    className="mt-6 w-full py-2.5 rounded-xl border-2 border-[#1B2A4E]/15 text-[#1B2A4E] text-sm font-bold hover:bg-[#E8842C] hover:border-[#E8842C] hover:text-white transition-all"
                  >
                    {t("vacancies.apply")}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
